import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import {
  ASSISTANT_QUESTION_COUNTER_THRESHOLD,
  ASSISTANT_QUESTION_MAX_CHARACTERS,
} from '@/constants/assistant';
import { cn } from '@/lib/utils';

interface AssistantComposerProps {
  isBusy: boolean;
  contextLabel: string | null;
  onSend: (question: string) => Promise<boolean>;
  onStop: () => void;
}

export function AssistantComposer({
  isBusy,
  contextLabel,
  onSend,
  onStop,
}: AssistantComposerProps) {
  const { t } = useTranslation('assistant');
  const [draft, setDraft] = useState('');

  async function submit() {
    const question = draft.trim();
    if (question.length === 0 || isBusy) {
      return;
    }
    const isAccepted = await onSend(question);
    if (isAccepted) {
      setDraft(function clearIfUnchanged(currentDraft) {
        return currentDraft.trim() === question ? '' : currentDraft;
      });
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (
      event.key === 'Enter' &&
      event.shiftKey === false &&
      event.nativeEvent.isComposing === false
    ) {
      event.preventDefault();
      void submit();
    }
  }

  return (
    <div className="border-t border-zooborder bg-zoowhite px-4 py-3 shrink-0">
      {contextLabel === null ? null : (
        <div className="mb-2">
          <span
            className="text-[11px] font-semibold rounded-lg px-2 py-1 bg-zoovpalelavender text-zooprimary inline-flex items-center gap-1.5"
            title={t('composer.contextHint')}
          >
            <Icon name="MapPin" />
            {contextLabel}
          </span>
        </div>
      )}
      <div className="flex items-end gap-2">
        <textarea
          value={draft}
          rows={1}
          maxLength={ASSISTANT_QUESTION_MAX_CHARACTERS}
          data-assistant-composer="true"
          onChange={function updateDraft(event) {
            setDraft(event.target.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder={t('composer.placeholder')}
          aria-label={t('composer.placeholder')}
          className="flex-1 resize-none max-h-32 min-h-10 rounded-xl border border-zooborder bg-zoowhite px-3.5 py-2.5 text-sm text-zooindigo placeholder:text-zoodisabledtext focus:outline-none focus:border-zooprimary focus:ring-2 focus:ring-zooprimary/15"
        />
        {isBusy ? (
          <button
            type="button"
            onClick={onStop}
            className="h-10 px-3 rounded-xl border border-zooborder bg-zoowhite text-zooindigo text-sm font-semibold flex items-center gap-1.5 hover:bg-zoovpalelavender"
          >
            <Icon name="CircleStop" />
            {t('composer.stop')}
          </button>
        ) : (
          <button
            type="button"
            onClick={function send() {
              void submit();
            }}
            aria-label={t('composer.send')}
            disabled={draft.trim().length === 0}
            className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center transition-colors',
              draft.trim().length === 0
                ? 'bg-zoodisabledbg text-zoodisabledtext'
                : 'bg-zooprimary text-zoowhite shadow-md hover:bg-zooprimaryhover'
            )}
          >
            <Icon name="SendHorizontal" />
          </button>
        )}
      </div>
      <div className="flex items-start justify-between gap-3 mt-2">
        <p className="text-[11px] text-zoomuted">{t('composer.disclaimer')}</p>
        {draft.length >= ASSISTANT_QUESTION_COUNTER_THRESHOLD ? (
          <p className="text-[11px] text-zoowarningtext shrink-0" aria-live="polite">
            {t('composer.counter', {
              count: draft.length,
              maximum: ASSISTANT_QUESTION_MAX_CHARACTERS,
            })}
          </p>
        ) : null}
      </div>
    </div>
  );
}
